import { PageType } from '@app-types/PageType'
import Input from '@components/Input/Input'
import GenericModal from '@components/Modal/Modal'
import { useUserData } from '@contexts/UserDataContext'
import { useRouter } from 'next/router'
import { FC, FormEvent, useRef, useState } from 'react'
import toast from 'react-hot-toast'
import { FaCircleMinus, FaCirclePlus } from 'react-icons/fa6'
import ToolbarButton from './ToolbarButton'

type Props = {
  type: PageType
  basePath?: string
}

const SavePageModal: FC<Props> = ({ type, basePath = '' }) => {
  const [isModalOpen, setIsModalOpen] = useState(false)
  const inputElRef = useRef<HTMLInputElement>(null)
  const { userData, updateUserData } = useUserData()
  const router = useRouter()
  const page = Number(router.query.page)

  const formHandler = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    const name = inputElRef.current?.value
    if (name) {
      const newSavedPages = [
        ...userData.savedPages,
        { name, page: Number(page), type, basePath },
      ]
      updateUserData('savedPages', newSavedPages)
      setIsModalOpen(false)
      toast('Kaydedildi!')
    } else {
      alert('Lütfen bir sayfa adı giriniz.')
    }
  }

  const isPageSaved = userData.savedPages.some(
    (savedPage) => savedPage.page === page && savedPage.type === type,
  )

  const handleButtonClick = () => {
    if (isPageSaved) {
      if (
        window.confirm('Bu sayfayı kaydetmiştiniz. Silmek istediğinize emin misiniz?')
      ) {
        const newSavedPages = userData.savedPages.filter(
          (savedPage) => savedPage.page !== page || savedPage.type !== type,
        )
        updateUserData('savedPages', newSavedPages)
        toast('Silindi!')
      }
    } else {
      setIsModalOpen(true)
    }
  }

  return (
    <>
      <ToolbarButton
        ariaLabel={isPageSaved ? 'Kaydedilenlerden çıkar' : 'Sayfayı kaydet'}
        title={isPageSaved ? 'Kaydedilenlerden çıkar' : 'Sayfayı kaydet'}
        onClick={handleButtonClick}
        className={
          isPageSaved
            ? 'text-emerald-600 bg-emerald-50 dark:bg-emerald-500/10 ring-1 ring-emerald-500/30'
            : 'text-main hover:bg-surface-subtle'
        }
      >
        {isPageSaved ? <FaCircleMinus /> : <FaCirclePlus />}
      </ToolbarButton>

      <GenericModal
        isOpen={isModalOpen}
        variant="bottom-sheet"
        id="save-page"
        triggerCloseModal={() => setIsModalOpen(false)}
      >
        <div className="flex flex-col justify-center items-center py-2">
          <h3 className="mb-5 font-bold text-xl text-main">Sayfayı kaydet</h3>
          <form onSubmit={formHandler} className="lg:px-10 w-full mb-2">
            <div className="mb-5">
              <label className="block mb-2 font-bold text-sm text-main">Sayfa adı</label>
              <Input
                className="shadow-sm px-5 py-3 border border-main rounded-xl focus:ring-2 focus:ring-brand-secondary/50 outline-none w-full h-12 bg-surface-subtle text-main leading-tight appearance-none"
                placeholder="Sayfa adı"
                name="pageName"
                type="text"
                maxLength={50}
                autoComplete="off"
                id="pageName"
                ref={inputElRef}
              />
            </div>
            <div className="mb-2 mt-4">
              <button
                className="bg-main text-brand-secondary ring-1 ring-black/5 dark:ring-white/10 hover:bg-brand-secondary hover:text-white transition-colors shadow-sm px-5 py-3 rounded-xl focus:outline-none w-full font-semibold"
                type="submit"
              >
                Kaydet
              </button>
            </div>
          </form>
          <small className="text-muted text-center leading-tight">
            * Bu sayfayı girmiş olduğunuz isim ile daha sonra hatırlamanız için
            kaydedebilirsiniz.
          </small>
        </div>
      </GenericModal>
    </>
  )
}

export default SavePageModal
